import { StateCreator } from 'zustand';

import { getVoorraadSeverity, VoorraadSeverityVariant } from '../components/VoorraadBeheer/voorraadSeverity';
import { Voorraad } from '../types/Voorraadbeheer';
import { getClaimsTotaal } from '../utils/voorraadbeheer';

import { PlankDataStore } from './types/PlankDataStore';
import { AtomicVoorraadbeheerTooltip, PlankTooltipMechanism, PlankTooltipStore } from './types/PlankTooltipStore';

const getDefaultTooltip = (): AtomicVoorraadbeheerTooltip => ({
  title: '',
  aanwezig: 0,
  benodigd: 0,
  nietInzetbaar: 0,
  verwachtGereed: 0,
  overOfTekort: 0,
  show: false,
  inzetten: 0,
  claims: {},
  x: -100,
  y: -100,
  clientY: 0,
  clientX: 0,
  orientation: 'top',
  orientationX: 'left',
  timeoutId: undefined,
});
const TOOLTIP_BASE_HEIGHT = 374;
const TOOLTIP_CLAIM_LINE_HEIGHT = 21;
const TOOLTIP_AVAILABILITY_ROW_COUNT = 2;

/** Number of 'beschikbaar' rows shown in the tooltip for each severity variant (before the
 * claims-count exception below is applied to 'error'). */
const AVAILABILITY_ROW_COUNT_BY_VARIANT: Record<VoorraadSeverityVariant, number> = {
  success: 0,
  warning: 1,
  error: 1,
};

const getXy = (
  args: Voorraad & {
    title: string;
  } & Omit<PlankTooltipMechanism, 'show' | 'orientation' | 'orientationX' | 'timeoutId'>,
): { orientation: 'top' | 'bottom'; orientationX: 'left' | 'right'; x: number; y: number } => {
  const orientation = args.clientY + 200 > window.screen.height - 175 ? 'bottom' : 'top';
  const orientationX = args.clientX > window.screen.width - 152 ? 'right' : 'left';
  const x = args.x - 24 > 8 ? args.x - 24 : 8;
  const claimsCount = getClaimsTotaal(args.claims);
  const variant = getVoorraadSeverity(args.overOfTekort, claimsCount);
  // 'Beschikb. claims' hoort ook bij de rode categorie zolang er claims zijn om af te trekken.
  const renderedAvailabilityRowCount =
    AVAILABILITY_ROW_COUNT_BY_VARIANT[variant] + (variant === 'error' && claimsCount > 0 ? 1 : 0);
  const tooltipHeight =
    TOOLTIP_BASE_HEIGHT - (TOOLTIP_AVAILABILITY_ROW_COUNT - renderedAvailabilityRowCount) * TOOLTIP_CLAIM_LINE_HEIGHT;
  return {
    y:
      orientation === 'bottom'
        ? args.y - (tooltipHeight + Object.keys(args.claims).length * TOOLTIP_CLAIM_LINE_HEIGHT)
        : args.y - 28,
    x: orientationX === 'right' ? args.x - (152 - 24 - 18 - 4) : x,
    orientation,
    orientationX,
  };
};
const plankTooltipStateCreator: StateCreator<PlankTooltipStore & Partial<PlankDataStore>> = (set, get, __) => ({
  tooltip: getDefaultTooltip(),
  showTooltip(args) {
    const oldTimeoutId = get().tooltip.timeoutId;
    if (oldTimeoutId) clearTimeout(oldTimeoutId);
    const timeoutId = setTimeout(() => {
      set({
        tooltip: {
          ...args,
          show: true,
          timeoutId: undefined,
          ...getXy(args),
        },
      });
    }, 200);
    set((state) => ({ tooltip: { ...state.tooltip, timeoutId } }));
  },
  updateTooltip(args) {
    const tooltip = get().tooltip;
    if (!tooltip.show) return;
    const oldTimeoutId = tooltip.timeoutId;
    if (oldTimeoutId) clearTimeout(oldTimeoutId);
    const timeoutId = setTimeout(
      () =>
        set((state) => ({
          tooltip: {
            ...state.tooltip,
            ...args,
            ...getXy(args),
          },
        })),
      100,
    );
    set((state) => ({ tooltip: { ...state.tooltip, timeoutId } }));
  },
  hideTooltip() {
    const oldTimeoutId = get().tooltip.timeoutId;
    if (oldTimeoutId) clearTimeout(oldTimeoutId);
    const timeoutId = setTimeout(() => set({ tooltip: { ...getDefaultTooltip() } }), 200);
    set((state) => ({ tooltip: { ...state.tooltip, timeoutId } }));
  },
});
export default plankTooltipStateCreator;
